// src/components/GuidedSalesDemo.js
import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';

export default function GuidedSalesDemo({ onClose }) {
  const [step, setStep] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    setFadeIn(true);
    return () => setFadeIn(false);
  }, [step]);

  const steps = [
    {
      text: "You Send Your Overdue Patients to me",
      visual: <MiniSpreadsheet />,
    },
    {
      text: "I send out one recall message to each patient on your behalf",
      visual: <MessageIcons />,
    },
    {
      text: "I follow a script",
      visual: <ScriptVisual />,
    },
    {
      text: "I send every patient who gave a positive response back to you",
      visual: <ForwardVisual />,
    },
    {
      text: "All you do is Give me the List, and Receive a List of Patients Who Said Yes",
      visual: <FinalVisual />,
    },
  ];

  const current = steps[step];

  function handleNext() {
    if (step < steps.length - 1) {
      setFadeIn(false);
      setTimeout(() => {
        setStep(step + 1);
      }, 400);
    } else {
      onClose();
    }
  }

  function handleBack() {
    if (step > 0) {
      setFadeIn(false);
      setTimeout(() => {
        setStep(step - 1);
      }, 400);
    }
  }

  return (
    <div style={overlayStyle}>
      <div style={backgroundGlowStyle} />
      
      <div 
        style={{
          ...contentStyle,
          opacity: fadeIn ? 1 : 0,
          transform: fadeIn ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Visual */}
        <div style={visualWrapperStyle}>
          {current.visual}
        </div>

        {/* Text - Verbatim */}
        <div style={textWrapperStyle}>
          <h1 style={headlineStyle}>{current.text}</h1>
        </div>

        {/* Progress */}
        <div style={progressStyle}>
          {steps.map((_, i) => (
            <div 
              key={i} 
              style={{
                ...dotStyle,
                background: i === step ? '#2D9B8A' : 'rgba(255,255,255,0.15)',
                width: i === step ? 32 : 8,
              }}
            />
          ))}
        </div>

        {/* Navigation */}
        <div style={navStyle}>
          <button 
            onClick={handleBack} 
            style={backBtnStyle}
            disabled={step === 0}
          >
            <ArrowLeft size={18} />
            Back
          </button>
          
          <button onClick={handleNext} style={nextBtnStyle}>
            {step === steps.length - 1 ? 'Got it' : 'Next'}
            <ArrowRight size={18} />
          </button>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <span style={footerTextStyle}>
            Step {step + 1} of {steps.length}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- VISUAL COMPONENTS ----

function MiniSpreadsheet() {
  return (
    <div style={sheetContainerStyle}>
      <div style={sheetHeaderStyle}>
        <span style={sheetHeaderCellStyle}>NAME</span>
        <span style={sheetHeaderCellStyle}>CONTACT</span>
        <span style={sheetHeaderCellStyle}>TREATMENT</span>
      </div>
      <div style={sheetRowStyle}>
        <span style={sheetCellStyle}>Sarah Mitchell</span>
        <span style={sheetCellStyle}>+65 9123 4567</span>
        <span style={sheetCellStyle}>Routine Cleaning</span>
      </div>
      <div style={sheetRowStyle}>
        <span style={sheetCellStyle}>James Wong</span>
        <span style={sheetCellStyle}>+65 9234 5678</span>
        <span style={sheetCellStyle}>Orthodontic</span>
      </div>
      <div style={sheetRowStyle}>
        <span style={sheetCellStyle}>Emma Tan</span>
        <span style={sheetCellStyle}>+65 9345 6789</span>
        <span style={sheetCellStyle}>Root Canal</span>
      </div>
    </div>
  );
}

function MessageIcons() {
  return (
    <div style={iconContainerStyle}>
      <div style={iconWrapperStyle}>
        <div style={whatsappIconStyle}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="22" fill="#25D366" stroke="none"/>
            <path d="M24 8C15.16 8 8 15.16 8 24C8 27.52 9.12 30.72 10.88 33.44L8 40L14.56 37.12C17.28 38.88 20.48 40 24 40C32.84 40 40 32.84 40 24C40 15.16 32.84 8 24 8Z" fill="white"/>
            <path d="M33.6 28.8C33.2 28.48 30.8 27.36 30.4 27.2C30 27.04 29.76 26.96 29.44 27.36C29.12 27.76 28.16 28.8 27.84 29.12C27.52 29.44 27.2 29.52 26.8 29.28C24.8 28.16 23.28 27.04 22.08 25.44C21.76 25.04 22.08 25.12 22.08 25.12C22.08 25.12 22.88 24.32 23.04 24C23.2 23.68 23.2 23.36 23.04 23.04C22.88 22.72 21.92 20.8 21.6 20.16C21.28 19.52 20.96 19.6 20.64 19.6H20C19.68 19.6 19.12 19.76 18.72 20.16C18.32 20.56 17.28 21.6 17.28 23.68C17.28 25.76 18.88 27.84 19.04 28.08C19.2 28.32 21.28 31.68 24.8 33.12C26.4 33.84 27.68 34.16 28.64 34.16C29.6 34.16 30.4 33.76 30.88 33.28C31.36 32.8 31.68 32.16 31.84 31.68C32 31.2 31.84 30.72 31.6 30.4C31.36 30.08 30.08 29.44 33.6 28.8Z" fill="#25D366"/>
          </svg>
        </div>
        <span style={iconLabelStyle}>WhatsApp</span>
      </div>
      <div style={iconWrapperStyle}>
        <div style={smsIconStyle}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect x="6" y="8" width="36" height="28" rx="4" stroke="#2D9B8A" strokeWidth="2" fill="none"/>
            <path d="M14 20H34" stroke="#2D9B8A" strokeWidth="2" strokeLinecap="round"/>
            <path d="M14 28H28" stroke="#2D9B8A" strokeWidth="2" strokeLinecap="round"/>
            <path d="M20 36L24 30" stroke="#2D9B8A" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <span style={iconLabelStyle}>SMS</span>
      </div>
    </div>
  );
}

function ScriptVisual() {
  return (
    <div style={scriptContainerStyle}>
      <div style={scriptMessageStyle}>
        Hi Sarah,
        <br /><br />
        This is Bright Smiles Dental.
        <br /><br />
        It's been 7 months.
        <br /><br />
        Time for your Routine cleaning recall!
      </div>
      <div style={scriptLabelStyle}>Personalized for each patient</div>
    </div>
  );
}

function ForwardVisual() {
  return (
    <div style={forwardContainerStyle}>
      <div style={forwardRowStyle}>
        <span style={forwardLabelStyle}>Patient</span>
        <span style={forwardArrowStyle}>→</span>
        <span style={forwardLabelStyle}>Reply</span>
      </div>
      <div style={forwardRowStyle}>
        <span style={forwardNameStyle}>Sarah Mitchell</span>
        <span style={forwardArrowStyle}>→</span>
        <span style={forwardReplyStyle}>"Sure I want to come back in 10 days"</span>
      </div>
      <div style={forwardRowStyle}>
        <span style={forwardNameStyle}>James Wong</span>
        <span style={forwardArrowStyle}>→</span>
        <span style={forwardReplyStyle}>"I'm free for a follow-up this week"</span>
      </div>
    </div>
  );
}

function FinalVisual() {
  return (
    <div style={finalContainerStyle}>
      <div style={finalRowStyle}>
        <div style={finalIconStyle}>📋</div>
        <span style={finalTextStyle}>You: Send me the list</span>
      </div>
      <div style={finalArrowRowStyle}>↓</div>
      <div style={finalRowStyle}>
        <div style={finalIconStyle}>⚡</div>
        <span style={finalTextStyle}>Me: Send one recall to each patient</span>
      </div>
      <div style={finalArrowRowStyle}>↓</div>
      <div style={finalRowStyle}>
        <div style={finalIconStyle}>✅</div>
        <span style={finalTextStyle}>You: Receive a list of patients who said "Yes"</span>
      </div>
    </div>
  );
}

// ---- STYLES ----

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: '#070E16',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Inter', sans-serif",
  overflow: 'hidden',
};

const backgroundGlowStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 900,
  height: '70%',
  maxHeight: 700,
  background: 'radial-gradient(circle, rgba(45, 155, 138, 0.08) 0%, rgba(45, 155, 138, 0.02) 40%, rgba(45, 155, 138, 0) 70%)',
  filter: 'blur(60px)',
  pointerEvents: 'none',
  zIndex: 0,
};

const contentStyle = {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 900,
  width: '100%',
  padding: '40px 24px',
  gap: 32,
};

const visualWrapperStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 160,
};

const textWrapperStyle = {
  textAlign: 'center',
  maxWidth: 800,
};

const headlineStyle = {
  fontSize: 36,
  fontWeight: 700,
  color: '#FFFFFF',
  margin: 0,
  lineHeight: 1.3,
  letterSpacing: '-0.5px',
  fontFamily: "'Inter', sans-serif",
};

const progressStyle = {
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
  marginTop: 8,
};

const dotStyle = {
  height: 8,
  borderRadius: 4,
  transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
};

const navStyle = {
  display: 'flex',
  gap: 16,
  marginTop: 16,
};

const backBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 24px',
  background: 'rgba(255,255,255,0.06)',
  color: 'rgba(255,255,255,0.6)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 30,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'all 0.2s ease',
};

const nextBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 32px',
  background: '#2D9B8A',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 30,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "'Inter', sans-serif",
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 24px rgba(45, 155, 138, 0.3)',
};

const footerStyle = {
  marginTop: 8,
};

const footerTextStyle = {
  fontSize: 13,
  color: 'rgba(255,255,255,0.25)',
  fontFamily: "'Inter', sans-serif",
  letterSpacing: '0.5px',
};

// Visual Styles - Spreadsheet
const sheetContainerStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  padding: '20px 24px',
  width: '100%',
  maxWidth: 520,
};

const sheetHeaderStyle = {
  display: 'grid',
  gridTemplateColumns: '1.8fr 1.8fr 1.4fr',
  gap: 12,
  paddingBottom: 12,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  marginBottom: 12,
};

const sheetHeaderCellStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: "'JetBrains Mono', monospace",
};

const sheetRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1.8fr 1.8fr 1.4fr',
  gap: 12,
  padding: '8px 0',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
};

const sheetCellStyle = {
  fontSize: 14,
  color: 'rgba(255,255,255,0.8)',
  fontFamily: "'JetBrains Mono', monospace",
};

// Visual Styles - Icons
const iconContainerStyle = {
  display: 'flex',
  gap: 48,
  justifyContent: 'center',
  alignItems: 'center',
};

const iconWrapperStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
};

const iconLabelStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.7)',
  fontFamily: "'JetBrains Mono', monospace",
};

const whatsappIconStyle = {
  filter: 'drop-shadow(0 0 20px rgba(37, 211, 102, 0.15))',
};

const smsIconStyle = {
  filter: 'drop-shadow(0 0 20px rgba(45, 155, 138, 0.15))',
};

// Visual Styles - Script
const scriptContainerStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  padding: '24px 28px',
  width: '100%',
  maxWidth: 520,
};

const scriptMessageStyle = {
  fontSize: 15,
  lineHeight: 1.8,
  color: 'rgba(255,255,255,0.85)',
  fontFamily: "'JetBrains Mono', monospace",
  whiteSpace: 'pre-wrap',
};

const scriptLabelStyle = {
  marginTop: 12,
  fontSize: 12,
  color: 'rgba(255,255,255,0.3)',
  fontFamily: "'JetBrains Mono', monospace",
  textAlign: 'center',
  fontStyle: 'italic',
};

// Visual Styles - Forward
const forwardContainerStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  padding: '20px 24px',
  width: '100%',
  maxWidth: 560,
};

const forwardRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1.8fr 0.5fr 3fr',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
};

const forwardLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: 'rgba(255,255,255,0.3)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: "'JetBrains Mono', monospace",
};

const forwardNameStyle = {
  fontSize: 14,
  color: 'rgba(255,255,255,0.8)',
  fontFamily: "'JetBrains Mono', monospace",
};

const forwardArrowStyle = {
  textAlign: 'center',
  color: '#2D9B8A',
  fontSize: 20,
};

const forwardReplyStyle = {
  fontSize: 14,
  color: '#3ABFA8',
  fontWeight: 500,
  fontFamily: "'JetBrains Mono', monospace",
  fontStyle: 'italic',
};

// Visual Styles - Final
const finalContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
};

const finalRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: '16px 24px',
  width: '100%',
  maxWidth: 480,
};

const finalIconStyle = {
  fontSize: 24,
};

const finalTextStyle = {
  fontSize: 16,
  color: 'rgba(255,255,255,0.85)',
  fontFamily: "'JetBrains Mono', monospace",
};

const finalArrowRowStyle = {
  fontSize: 18,
  color: '#2D9B8A',
  padding: '4px 0',
};